/**
 * Mount de `/cvn-quals.html` — Quals & Traps (Fase 5g.3).
 *
 * Flujo:
 *  1. `mountChrome` con el skin del monitor (`chrome-wood--cvn-quals`).
 *     Página pública de solo lectura — no exige sesión.
 *  2. Inyecta el shell de la tabla sobre la pantalla del monitor.
 *  3. Carga `GET /api/greenie-board`, agrega por piloto y pinta la
 *     tabla (o un estado vacío / de error).
 *  4. Las cabeceras son ordenables: al pulsar una, reordena la tabla
 *     (toggle de dirección si ya estaba activa) — patrón del logbook.
 *
 * Se llega desde el hotspot del monitor TV de la ready room.
 */

import { mountChrome } from '../chrome/index.js';
import { fetchGreenieBoard } from '../greenie-board/api.js';
import {
  computeRows,
  defaultDirFor,
  renderQualsNote,
  renderQualsShell,
  renderQualsTable,
  type SortKey,
  type SortState,
} from './markup.js';

import './cvn-quals.css';

export async function mountCvnQuals(): Promise<void> {
  const { contentRoot } = await mountChrome({
    activeSection: 'cvn-quals',
    allowAnonymous: true,
  });

  contentRoot.innerHTML = renderQualsShell();
  const bodyZone = contentRoot.querySelector<HTMLElement>('[data-zone="body"]');
  const updatedEl = contentRoot.querySelector<HTMLElement>(
    '[data-zone="updated"]',
  );
  if (!bodyZone) {
    throw new Error('[cvn-quals] shell incompleto tras el render');
  }

  bodyZone.innerHTML = renderQualsNote('Cargando apontajes…');

  const result = await fetchGreenieBoard();
  if (!result.ok) {
    bodyZone.innerHTML = renderQualsNote(result.message, true);
    if (updatedEl) updatedEl.textContent = 'sin conexión';
    return;
  }

  if (updatedEl) updatedEl.textContent = formatUpdated(result.data.generatedAt);

  const rows = computeRows(result.data.pilots);
  if (rows.length === 0) {
    bodyZone.innerHTML = renderQualsNote(
      '— Todavía no hay apontajes registrados. —',
    );
    return;
  }

  // Orden inicial: por media descendente (el mejor apontador arriba).
  let sort: SortState = { key: 'avg', dir: 'desc' };
  bodyZone.innerHTML = renderQualsTable(rows, sort);

  // Delegación: el listener vive en `bodyZone` y sobrevive a los
  // repintados de la tabla. Al pulsar una cabecera, alterna la
  // dirección si ya era la columna activa, o aplica la dirección por
  // defecto si es una columna nueva.
  bodyZone.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement | null;
    const btn = target?.closest<HTMLElement>('[data-sort-key]');
    if (!btn?.dataset.sortKey) return;
    const key = btn.dataset.sortKey as SortKey;
    sort =
      sort.key === key
        ? { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: defaultDirFor(key) };
    bodyZone.innerHTML = renderQualsTable(rows, sort);
  });
}

/** Formatea el `generatedAt` para el subtítulo (`dd/mm/yyyy hh:mm`). */
function formatUpdated(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'fecha desconocida';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `actualizado ${pad(d.getDate())}/${pad(d.getMonth() + 1)}/` +
    `${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}
